import './index.css'
import UploadItem from "../UploadItem";
import {
  Button,
  Dropdown,
  message,
  Modal,
  Pagination,
  Space,
  Spin,
  Switch,
  Tabs,
  Upload,
  UploadProps
} from 'antd';
import React, {useEffect, useMemo, useRef, useState} from "react";
import {getId, getRandomPathEnabled, postFile} from '../../helpers/file';
import RightLink from "./RightLink";
import {DeleteOutlined, DownloadOutlined} from "@ant-design/icons";
import {downloadToCsv, downloadToExcel} from "../../helpers/csv";
import BatchActions from './BatchActions';
import ErrorBoundary from "../ErrorBoundary";
import UrlUpload, {eventBus} from "./UrlUpload";
import ClipboardUploadButton from "../ClipboardUploadButton";
import {getOS} from "../../utils/osUtils";
import { useTranslation } from '../../hooks/useTranslation';
import { isEnglishSite } from '../../config/site';
import { showUploadErrorModal } from '../../utils/uploadErrorModal';
import { useReuploadPolicy } from '../../hooks/useReuploadPolicy';
import { useSupportEmail } from '../../hooks/useSupportEmail';
import {getStoredActiveDirectory, saveActiveDirectory} from '../../utils/dirTree';
import {AiImageEditCapability, getAiImageEditCapability} from '../../services/aiImageEdit';
import {
  HomeActionKey,
  loadHomeActionHighlightKeys,
} from '../../services/homeActionHighlight';
import { useUserInfo } from '../../contexts/UserInfoContext';
import { useImgCloudConfig } from '../../contexts/ImgCloudConfigContext';
import { buildHttpLink } from '../../helpers/url';

const MAX_CONCURRENT = 3;

const { Dragger } = Upload;

const UPLOAD_KEY = 'upload_key';

type FolderUploadMeta = {
  fileName: string;
  folderUploadId: string;
  folderUploadDate: string;
};

const getRelativeFileName = (file: any) => file?.webkitRelativePath || file?.name || '';

const getFolderRoot = (relativePath: string) => {
  const [root] = relativePath.split('/');
  return root || 'folder';
};

const getDateSegment = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}${month}${day}`;
};

const createFolderUploadId = () => {
  return `folder-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const shouldSkipPasteUpload = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return false;
  }

  const tagName = target.tagName;
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName)) {
    return true;
  }

  if (target instanceof HTMLElement && target.isContentEditable) {
    return true;
  }

  return Boolean(target.closest('[data-text-edit-modal="true"], [contenteditable="true"]'));
};

/**
 *
 * @author dongdong.fdd
 * @Company AutoNavi Co.,Ltd.
 * @date 2023/7/29 15:26
 */
function Body() {
  const supportEmail = useSupportEmail();
  const { t, currentLanguage } = useTranslation();
  const { policy: reuploadPolicy } = useReuploadPolicy();
  const { user } = useUserInfo();
  const { httpLink } = useImgCloudConfig();
  const userInfo = user || {};
  const isEn = currentLanguage === 'en';
  const [uuid, setUuid] = useState<string | null>();
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentDir, setCurrentDir] = useState(() => getStoredActiveDirectory());
  const [enableFolderUpload, setEnableFolderUpload] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [aiEditCapability, setAiEditCapability] = useState<AiImageEditCapability>();
  const [highlightActionKeys, setHighlightActionKeys] = useState<HomeActionKey[]>([]);
  const actionableList = useMemo(
    () => list.filter((item) => item.reviewStatus !== 'PENDING'),
    [list]
  );

  useEffect(() => {
    const actionableIds = new Set(actionableList.map((item) => item.id));
    setSelectedIds((previous) => {
      const next = previous.filter((id) => actionableIds.has(id));
      return next.length === previous.length ? previous : next;
    });
  }, [actionableList]);

  const queueRef = useRef<any>([]);
  const activeCountRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const folderUploadMetaRef = useRef<WeakMap<object, FolderUploadMeta>>(new WeakMap());

  const getFolderUploadMeta = (file: any) => {
    if (file && typeof file === 'object') {
      const directMeta = folderUploadMetaRef.current.get(file);
      if (directMeta) {
        return directMeta;
      }
      const originFile = file.originFileObj;
      if (originFile && typeof originFile === 'object') {
        return folderUploadMetaRef.current.get(originFile);
      }
    }
    return undefined;
  };

  const prepareFolderUploadMeta = (fileList: any[]) => {
    const relativeFiles = fileList.filter((file) => getRelativeFileName(file).includes('/'));
    if (relativeFiles.length === 0) {
      return;
    }

    const folderIdsByRoot = new Map<string, FolderUploadMeta>();
    relativeFiles.forEach((file) => {
      const existingMeta = getFolderUploadMeta(file);
      if (existingMeta) {
        folderIdsByRoot.set(getFolderRoot(existingMeta.fileName), existingMeta);
      }
    });

    relativeFiles.forEach((file) => {
      if (getFolderUploadMeta(file)) {
        return;
      }
      const fileName = getRelativeFileName(file);
      const root = getFolderRoot(fileName);
      let meta = folderIdsByRoot.get(root);
      if (!meta) {
        meta = {
          fileName,
          folderUploadId: createFolderUploadId(),
          folderUploadDate: getDateSegment(),
        };
        folderIdsByRoot.set(root, meta);
      }
      folderUploadMetaRef.current.set(file, {
        ...meta,
        fileName,
      });
    });
  };

  const runNext = () => {
    if (queueRef.current.length === 0 || activeCountRef.current >= MAX_CONCURRENT) {
      return;
    }

    const nextTask = queueRef.current.shift();
    activeCountRef.current++;
    nextTask().finally(() => {
      activeCountRef.current--;
      runNext();
    });
  };

  const customRequest = async ({ file, onSuccess, onError }: any) => {
    queueRef.current.push(async () => {
      try {
        const folderUploadMeta = getFolderUploadMeta(file);
        await postFile(file, (percent) => {
                  let content = t('upload.uploadProgress', { percent });
                  if (percent == 100) {
                    content = t('upload.uploadComplete')
                  }
                  message.info({
                    duration: 1000,
                    content,
                    key: UPLOAD_KEY
                  });
                }, (errorMessage, fileName, traceId, fileSize, httpStatus) => {
                  showUploadErrorModal(t, errorMessage, fileName, traceId, fileSize, httpStatus);
                }, currentDir, folderUploadMeta
        ) as any;
        message.info({
          duration: 3,
          content: t('upload.uploadSuccess'),
          key: UPLOAD_KEY
        });
        loadHistory()
      } catch (err) {
        onError(err);
        message.error(t('upload.fileUploadFailed', { fileName: file.name }));
      }
    });

    runNext(); // 每次加任务时尝试执行下一个
  };

  const updateAiEditCapability = () => {
    getAiImageEditCapability()
      .then(setAiEditCapability)
      .catch(() => setAiEditCapability(undefined));
  };

  useEffect(() => {
    updateAiEditCapability();
    
    // 检测是否为移动端
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
  }, [])

  useEffect(() => {
    let active = true;
    loadHomeActionHighlightKeys().then(actionKeys => {
      if (active) {
        setHighlightActionKeys(actionKeys);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  // 监听URL上传成功事件，自动重新加载历史数据
  useEffect(() => {
    const handleUrlUploadSuccess = (data: any) => {
      console.log('URL上传成功，重新加载历史数据:', data);
      loadHistory();
    };

    eventBus.on('urlUploadSuccess', handleUrlUploadSuccess);
    
    return () => {
      eventBus.off('urlUploadSuccess', handleUrlUploadSuccess);
    };
  }, []);

  useEffect(() => {
    setUuid(getId());
  }, [])

  // 加载历史数据，支持目录参数
  const loadHistory = (dir?: string) => {
    // 取消上一次的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 创建新的 AbortController，兼容不支持的浏览器
    const controller = window.AbortController ? new AbortController() : null;
    abortControllerRef.current = controller;

    setLoading(true);
    fetch(`/img/history?uid=${getId()}&page=${current}&pageSize=${pageSize}&dir=${encodeURIComponent(dir || currentDir || '')}&keyword=${keyword}`, {
      signal: controller?.signal
    })
      .then(res => res.json())
      .then(pageData => {
        setLoading(false);
        setList(pageData.data);
        setTotal(pageData.total);
      })
      .catch(error => {
        // 如果是取消请求，不需要处理错误
        if (error.name !== 'AbortError') {
          setLoading(false);
          console.error('加载历史数据失败:', error);
        }
      });
  };

  // 导出
  const keyMap = {
    fileName: t('body.exportFileName'),
    remark: t('body.exportRemark'),
    contentType: t('body.exportFileType'),
    url: t('body.exportAccessLink'),
    time: t('body.exportUploadTime'),
    storageType: t('body.exportStorageType'),
    ...(httpLink?.enabled === true ? { httpLink: t('body.exportHttpLink') } : {}),
  };

  // 排序类型定义
  type SortType = 'original' | 'name-asc' | 'name-desc';

  // 排序函数
  const sortList = (data: any[], sortType: SortType) => {
    if (sortType === 'original') {
      return [...data]; // 返回副本，保持原顺序
    }

    const sorted = [...data];
    // 自然排序：把文件名中的数字按数值比较，例如 XXX_2.png 排在 XXX_10.png 前。
    const compareByFileName = (a: any, b: any) => (a.fileName || '').localeCompare(
      b.fileName || '',
      'zh-CN',
      { numeric: true, sensitivity: 'base' }
    );

    if (sortType === 'name-asc') {
      // 文件名自然正序（A-Z，支持中文）
      sorted.sort(compareByFileName);
    } else if (sortType === 'name-desc') {
      // 文件名自然倒序（Z-A，支持中文）
      sorted.sort((a, b) => compareByFileName(b, a));
    }
    return sorted;
  };

  const exportPageList = (type: 'csv' | 'excel', sortType: SortType = 'original') => {
    if (actionableList.length === 0) {
      message.warning(t('common.noActionableFiles'));
      return;
    }

    const sortedList = sortList(actionableList, sortType).map((item) => {
      if (httpLink?.enabled !== true) {
        return item;
      }
      let generatedHttpLink = '';
      try {
        generatedHttpLink = buildHttpLink(item.url, httpLink.domain);
      } catch {
        // Keep the export usable when an old record contains an invalid URL.
      }
      return { ...item, httpLink: generatedHttpLink };
    });

    if (type === 'csv') {
      downloadToCsv(sortedList, keyMap);
    } else {
      downloadToExcel(sortedList, keyMap);
    }
  }

  // 清空当前页的数据
  const deletePageList = () => {
    if (actionableList.length === 0) {
      message.warning(t('common.noActionableFiles'));
      return;
    }
    const doDelete = window.confirm(t('common.deletePageData'))
    if (doDelete) {
      const promiseList = actionableList.map((item) => {
        return fetch(`/img/delete?id=${item.id}&uid=${getId()}`, {
          method: 'DELETE'
        });
      })
      Promise.all(promiseList).then(() => {
        message.success(t('common.pageDataCleared'));
        loadHistory()
      })
    }
  }

  useEffect(() => {
    const handlePaste = async (event: any) => {
      if (shouldSkipPasteUpload(event.target)) {
        return;
      }
      const items = (event.clipboardData).items || [];
      message.info({
        content: t('upload.startUpload'),
        key: UPLOAD_KEY
      });
      console.info('items is', items);
      if(items.length > 1 && items[0].kind != 'string') {
        // 选择了多个文件
        const files = [...items].map((item: any) => {
          return item.getAsFile();
        })
        for(const file of files) {
          await new Promise(async (resolve) => {
            if(file) {
              try {
                const result = await postFile(file, (percent) => {
                  let content = t('upload.uploadProgress', { percent });
                  if (percent == 100) {
                    content = t('upload.uploadComplete')
                  }
                  message.info({
                    duration: 1000,
                    content,
                    key: UPLOAD_KEY
                  });
                }, (errorMessage, fileName, traceId, fileSize, httpStatus) => {
                  showUploadErrorModal(t, errorMessage, fileName, traceId, fileSize, httpStatus);
                }, currentDir
                ).catch(() => {});
                console.info('upload result is ', result);
                resolve(true);
              } catch(e) {
                console.error(e);
                resolve(true);
              }
            }
          })
        }
        message.success({
          duration: 3,
          content: t('upload.uploadSuccess'),
          key: UPLOAD_KEY
        });
        loadHistory();
      } else {
        for(const item of items) {
          console.info('item is ', item, items.length);
          await new Promise(async (resolve) => {
            const file = item && item.getAsFile();
            if(file) {
              try {
                const result = await postFile(file, (percent) => {
                  let content = t('upload.uploadProgress', { percent });
                  if (percent == 100) {
                    content = t('upload.uploadComplete')
                  }
                  message.info({
                    duration: 1000,
                    content,
                    key: UPLOAD_KEY
                  });
                }, (errorMessage, fileName, traceId, fileSize, httpStatus) => {
                  showUploadErrorModal(t, errorMessage, fileName, traceId, fileSize, httpStatus);
                }, currentDir
                ).catch(() => {});
                console.info('upload result is ', result);
                resolve(true);
              } catch(e) {
                console.error(e);
                resolve(true);
              }
            } else {
              // 尝试读取剪切板中的内容
              if(item.type.match('^text/plain')) {
                item.getAsString(async (clipboardContent: any) => {
                  const blob = await new Blob([clipboardContent]);
                  const file = new File([blob], t('upload.clipboardTextFileName'), {
                    type : "text/plain;charset=utf-8"
                  });
                  await postFile(file, (percent) => {
                    let content = t('upload.uploadProgress', { percent });
                    if (percent == 100) {
                      content = t('upload.uploadComplete')
                    }
                    message.info({
                      duration: 1000,
                      content,
                      key: UPLOAD_KEY
                    });
                  }, (errorMessage, fileName, traceId, fileSize, httpStatus) => {
                    showUploadErrorModal(t, errorMessage, fileName, traceId, fileSize, httpStatus);
                  }, currentDir
                  ).catch(() => {});
                  resolve(true);
                })
              } else {
                console.info('not found file')
                message.warning(t('upload.clipboardNoFile'))
                resolve(true);
              }
            }
          })
          console.info('upload done', items.length);
        }
        message.success({
          duration: 3,
          content: t('upload.uploadSuccess'),
          key: UPLOAD_KEY
        });
        loadHistory()
      }
    }

    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('paste', handlePaste);
    }
  }, [currentDir, current])

  useEffect(() => {
    loadHistory();
  }, [uuid, current, pageSize, currentDir, keyword])

  useEffect(() => {
    setCurrent(1);
  }, [keyword]);

  // 组件卸载时取消正在进行的请求
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [])


  const props: UploadProps = {
    showUploadList: false,
    name: 'file',
    multiple: true,
    directory: enableFolderUpload,
    action: '/img/upload',
    beforeUpload: (file, fileList) => {
      prepareFolderUploadMeta(fileList);
      return true;
    },
    data: (file) => {
      message.info({
        content: t('upload.startUpload'),
        key: UPLOAD_KEY
      });
      const folderUploadMeta = getFolderUploadMeta(file);
      return {
        fileName: folderUploadMeta?.fileName || (file as any).webkitRelativePath || file.name,
        uid: getId(),
        dir: currentDir,
        randomPath: getRandomPathEnabled(),
        ...(folderUploadMeta ? {
          folderUploadId: folderUploadMeta.folderUploadId,
          folderUploadDate: folderUploadMeta.folderUploadDate,
        } : {})
      }
    },
    onChange(info) {
      const { status} = info.file;
      const event = info.event;
      console.info('status is', status, event?.percent);
      if (status == 'uploading') {
        let content = t('upload.uploadProgress', { percent: event?.percent?.toFixed?.(0) || 0 });
        if (event?.percent?.toFixed?.(0) == '100') {
          content = t('upload.uploadComplete')
        }
        message.info({
          duration: 1000,
          content,
          key: UPLOAD_KEY
        });
      }

      if (status === 'done') {
        message.success({
          duration: 3,
          content: t('upload.fileUploadSuccess', { fileName: info.file.name }),
          key: UPLOAD_KEY
        });
        loadHistory();
      } else if (status === 'error') {
        const res = info.file.response;
        if (res && res.status === 413) {
            showUploadErrorModal(t, t('upload.fileTooLarge'), info.file.name, undefined, info.file.size, res.status);
        } else if ((res as any).success === false) {
            showUploadErrorModal(t, (res as any).message, info.file.name, undefined, info.file.size, (res as any).status);
        } else {
            message.error({
                content: t('upload.fileUploadFailed', { fileName: info.file.name }),
                key: UPLOAD_KEY
            });
        }
      }
    },
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files);
    },
  };

  const handleSelect = (id: any, checked: boolean) => {
    if (!actionableList.some((item) => item.id === id)) {
      return;
    }
    setSelectedIds(prev => checked ? [...prev, id] : prev.filter(i => i !== id));
  };

  // 处理文件夹上传开关变化
  const handleFolderUploadChange = (checked: boolean) => {
    setEnableFolderUpload(checked);
  };

  return (
    <div
      className="Body"
      style={{
        paddingLeft: '30px',
        paddingRight: '30px',
      }}
    >
      <div className="title" style={{marginTop: "20px", marginBottom: "12px"}}>
        <h1
          title={`${(window as any).systemTitle || t('system.title')}, ${t('system.description')}`}
          style={{
            "height": "28px",
            "fontFamily": "PingFangSC",
            "fontWeight": 500,
            "fontSize": "20px",
            "color": "#21242E",
            "lineHeight": "28px",
            margin: '0px',
            display: 'inline-block'
          }}
        >
          {(window as any).systemTitle || t('system.title')}

        </h1>
        <span
          title={t('common.getUniqueId')}
          style={{
            "display": "inline-flex",
            "alignItems": "center",
            "fontSize": "12px",
            "cursor": "pointer",
            "paddingLeft": "5px",
            'color': 'red',
            "lineHeight": "14px"
          }}
          onClick={() => {
            Modal.info({
              title: t('contact.title'),
              content: (
                <div
                  className={"qr-code-ctn"}
                  style={{
                    marginRight: '34px'
                  }}
                >
                  <p className={"mailto-url"}>
                    {!isEn && <span style={{display: 'block'}}>{t('contact.wechat')}</span>}
                    {!isEn && <span style={{display: 'block'}}>{t('contact.qqGroup')}</span>}
                    <a style={{display: 'block'}} href={`mailto:${supportEmail}`}
                       target={'_blank'} rel="noopener noreferrer">
                      {t('contact.email', { email: supportEmail })}
                    </a>
                  </p>
                  <Tabs defaultActiveKey={isEnglishSite() ? 'tg' : (isEn ? 'uid' : 'wechat')}>
                    {!isEn && (
                      <Tabs.TabPane tab={t('contact.wechatOfficial')} key="wechat">
                        <div className="qr-code">
                          <img src={"https://z.wiki/autoupload/20231209/2Vsb.1164X1122-IMG_1279.JPG"} width={"100%"}/>
                        </div>
                      </Tabs.TabPane>
                    )}
                    {!isEnglishSite() && (
                      <Tabs.TabPane tab={t('contact.userIdentity')} key="uid">
                        <div className="qr-code">
                          <img src={"https://playground.z.wiki/qrcode/generateQRCode?data=" + getId() + " " + document.cookie}
                               width={"100%"}/>
                        </div>
                      </Tabs.TabPane>
                    )}
                    <Tabs.TabPane tab={t('contact.telegram')} key="tg">
                      <div className="qr-code">
                        <img src={"https://tc-new.z.wiki/autoupload/fdd/20250812/iR01/862X950/image.png"} width={"100%"}/>
                      </div>
                    </Tabs.TabPane>
                  </Tabs>
                </div>
              )
            })
          }}
        >
          {t('common.contactInfo')}
        </span>
      </div>

      <div
        className="opts"
        style={{
          paddingBottom: '16px'
        }}
      >
        <div className="get-share-file">

        </div>
        <div className="links">

        </div>
        <div className="auto-copy">

        </div>
        <div
          className="tips"
          style={{
            color: '#757D8B',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <div className="left">
            {!isMobile && (() => {
              const os = getOS();
              const shortcutKey = os === 'mac' ? 'Command+V' : 'Ctrl+V';
              return (
                <>
                  {t('common.pasteShortcut', { shortcut: shortcutKey })}
                </>
              );
            })()}
            {!isEn && (
              window.location.hostname === 'imgbed.cn' ? (
                <a href={"https://playground.z.wiki/img-cloud/index.html"} target={'_blank'} style={{color: 'red'}}>
                  {t('common.backupSite')}
                </a>
              ) : (
                <a href={"https://imgbed.cn/"} target={'_blank'} style={{color: 'red'}}>{t('common.newDomain')}</a>
              )
            )}
          </div>
          <div className="right" style={{flexShrink: 0}}>
            {!isEnglishSite() && (
              <ErrorBoundary>
                <RightLink/>
              </ErrorBoundary>
            )}
          </div>
        </div>
      </div>

      <div
        className="upload-zone"
        style={{
          "height": "178px",
          "backgroundColor": "#FFFFFF",
          "borderRadius": "6px",
          padding: '18px',
        }}
      >
        <Dragger {...props} customRequest={customRequest}>
          <p className="ant-upload-drag-icon" style={{marginTop: '0px', marginBottom: "8px"}}>
            <img src={"https://z.wiki/autoupload/20230731/JQyP.ic_%E5%A2%9E%E5%8A%A0%E8%A1%A8%E6%A0%BC.svg"}/>
          </p>
          <p
            className="ant-upload-text"
            style={{
              fontSize: '14px',
              margin: 0,
            }}
          >
            {t('common.dragFileHere')}<span style={{color: '#1A66FF'}}> {t('common.clickToUpload')} </span>
            {t('common.to')}<span style={{color: '#ff4747', fontWeight: 'bold'}}>
            {(currentDir === '__all__' || currentDir === '全部目录' || currentDir === '__default__' || currentDir === '默认目录') ? t('common.defaultDirectory') : currentDir}
            </span>（{t('common.selectDirectory')}）
          </p>
          <p
            className="ant-upload-hint"
            style={{
              fontSize: '12px',
              margin: 0,
              marginTop: '4px',
            }}
          >
            {t('common.uploadHint')}
          </p>
          {!isMobile && (
            <>
              <br/>
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation()
                }}
                style={{
                  display: 'inline-block'
                }}
              >
                <Switch
                  size="small"
                  style={{marginRight: 4}}
                  checked={enableFolderUpload}
                  onChange={handleFolderUploadChange}
                />
                <span style={{fontSize: 12, whiteSpace: 'nowrap'}}>
                      {t('common.enableFolderUpload')}（<span style={{fontWeight: 'bold'}}>{t('common.onlyClickUpload')}</span>）
                    </span>

                    <UrlUpload />
              </div>
            </>
          )}
        </Dragger>
      </div>
      <BatchActions
        onKeywordChange={(keyword) => {
          setKeyword(keyword);
        }}
        onSearch={(kw) => {
          setKeyword(kw);
          setCurrent(1);
          setSelectedIds([]);
          loadHistory();
        }}
        userInfo={userInfo}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        list={actionableList}
        loadHistory={() => loadHistory()}
        currentDir={currentDir}
        onDirChange={dir => {
          setCurrentDir(dir);
          saveActiveDirectory(dir);
          setCurrent(1); // 切换目录时重置为第一页
          setSelectedIds([]); // 切换目录时清空选择
          loadHistory(dir);
        }}
      />

      <Spin spinning={loading}>
        <div
          className="list"
          style={{
            "display": "flex",
            "alignItems": "flex-start",
            "flexDirection": "row",
            "flexWrap": "wrap",
            "alignContent": "flex-start",
            justifyContent: "space-between",
          }}
        >
          {
            (
              <>
                {
                  list?.map((item, index) => {
                    return (
                      <ErrorBoundary key={item.id}>
                        <UploadItem
                          dir={item.dir}
                          index={index}
                          userInfo={userInfo}
                          key={item.id}
                          url={item.url}
                          rawUrl={item.rawUrl}
                          contentType={item.contentType}
                          customDomain={item.customDomain || ''}
                          id={item.id}
                          fileName={item.fileName}
                          remark={item.remark}
                          time={item.time}
                          size={item.size}
                          illicit={item.illicit}
                          illicitDetails={item.illicitDetails}
                          reviewStatus={item.reviewStatus}
                          reviewSource={item.reviewSource}
                          reviewReason={item.reviewReason}
                          permanentSaveRequest={item.permanentSaveRequest}
                          storageType={item.storageType}
                          expireTime={item.expireTime}
                          htmlShareEnabled={item.htmlShareEnabled}
                          onDelete={() => {
                            loadHistory()
                          }}
                          onRefresh={() => {
                            loadHistory()
                          }}
                          selected={selectedIds.includes(item.id)}
                          onSelect={handleSelect}
                          selectedIds={selectedIds}
                          reuploadPolicy={reuploadPolicy}
                          aiEditCapability={aiEditCapability}
                          selectable={item.reviewStatus !== 'PENDING'}
                          highlightActionKeys={index === 0 ? highlightActionKeys : []}
                        />
                      </ErrorBoundary>
                    )
                  })
                }
                <i className={'hiddenUploadItem'} style={{width: '324px'}} />
                <i className={'hiddenUploadItem'} style={{width: '324px'}} />
                <i className={'hiddenUploadItem'} style={{width: '324px'}} />
                <i className={'hiddenUploadItem'} style={{width: '324px'}} />
                <i className={'hiddenUploadItem'} style={{width: '324px'}} />
                <i className={'hiddenUploadItem'} style={{width: '324px'}} />
                <i className={'hiddenUploadItem'} style={{width: '324px'}} />
                <i className={'hiddenUploadItem'} style={{width: '324px'}} />
                <i className={'hiddenUploadItem'} style={{width: '324px'}} />
              </>
            )
          }

        </div>
      </Spin>

      <div
        className="footer"
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          paddingBottom: "40px",
        }}
      >
        <Space>
          <Pagination
            current={current}
            size={'small'}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100', '200', '400', '600', '800', '1600', '3200']}
            total={total}
            showTotal={(total) => t('common.totalRecords', { total })}
            pageSize={pageSize}
            onChange={(page, pageSize) => {
              setCurrent(page);
              setPageSize(pageSize);
            }}
          />
          <Dropdown
            menu={{
              items: [
                {
                  key: 'csv',
                  label: t('common.exportAsCsv'),
                  children: [
                    {
                      key: 'csv-original',
                      label: t('common.originalOrder'),
                      onClick: () => exportPageList('csv', 'original')
                    },
                    {
                      key: 'csv-asc',
                      label: t('common.nameAsc'),
                      onClick: () => exportPageList('csv', 'name-asc')
                    },
                    {
                      key: 'csv-desc',
                      label: t('common.nameDesc'),
                      onClick: () => exportPageList('csv', 'name-desc')
                    },
                  ]
                },
                {
                  key: 'excel',
                  label: t('common.exportAsExcel'),
                  children: [
                    {
                      key: 'excel-original',
                      label: t('common.originalOrder'),
                      onClick: () => exportPageList('excel', 'original')
                    },
                    {
                      key: 'excel-asc',
                      label: t('common.nameAsc'),
                      onClick: () => exportPageList('excel', 'name-asc')
                    },
                    {
                      key: 'excel-desc',
                      label: t('common.nameDesc'),
                      onClick: () => exportPageList('excel', 'name-desc')
                    },
                  ]
                },
              ]
            }}
          >
            <Button icon={<DownloadOutlined />} size={'small'}>{t('common.export')}</Button>
          </Dropdown>
          <Button icon={<DeleteOutlined/>} size={'small'} onClick={deletePageList}>{t('common.clear')}</Button>
        </Space>
      </div>

      {/* 移动端剪切板上传悬浮按钮 */}
      <ClipboardUploadButton 
        currentDir={currentDir}
        onUploadSuccess={loadHistory}
        isMobile={isMobile}
      />
    </div>
  );
}

Body.propTypes = {}

export default Body
